// src/pages/SanValentin.jsx
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import mardeolivosfondo from "../assets/theme-images/mardeolivosfondo.png";
import videoSrc from "../assets/theme-images/valentin.mp4";

const PAGE_NAME = "Laurita";
const NOMBRE = "Laura";
const FECHA_INICIO = "2022-06-04";

const sections = [
  { key: 1, nombre: "Laurita", id: "peli" },
  { key: 2, nombre: "Menú del amor", id: "menu" },
  { key: 3, nombre: "Razones", id: "razones" },
  { key: 4, nombre: "El plan", id: "plan" },
];

const menuDelAmor = [
  {
    title: "Entrantes (para abrir el apetito 😏)",
    items: [
      {
        name: "Croqueta de abrazo infinito",
        desc: "Crujiente por fuera, calentita por dentro. Como tú cuando finges enfado.",
        price: "∞ abrazos",
        emoji: "🫂",
      },
      {
        name: "Bravas nivel ‘te miro y me rindo’",
        desc: "Pican lo justo: para que me persigas tú a mí por una vez.",
        price: "2 miradas",
        emoji: "🥔",
      },
    ],
  },
  {
    title: "Plato principal (lo serio… pero no mucho)",
    items: [
      {
        name: "Risotto de ‘te llevo el bolso’",
        desc: "Cremosito. Incluye: cargar con todo sin quejarme (vale, un poco sí).",
        price: "1 cita",
        emoji: "👜",
      },
      {
        name: "Solomillo ‘sí cariño’",
        desc: "Marinado en paciencia. Acompañado de asentir aunque no entienda nada.",
        price: "1 ‘vale’",
        emoji: "🥩",
      },
    ],
  },
  {
    title: "Postres (aquí ya no respondo de mí)",
    items: [
      {
        name: "Tarta ‘me derrito contigo’",
        desc: "Tan dulce que me da vergüenza escribirlo sin sonrojarme.",
        price: "3 besos",
        emoji: "🍰",
      },
      {
        name: "Coulant de ‘hoy mando yo’",
        desc: "Sale lava por dentro… como cuando me dices: “ponte guapo”.",
        price: "1 sonrisa",
        emoji: "🍫",
      },
    ],
  },
];

function daysBetween(dateStr) {
  const start = new Date(dateStr + "T00:00:00");
  const now = new Date();
  const diff = now.getTime() - start.getTime();
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
}

export default function SanValentin() {
  const [active, setActive] = useState(1);
  const navRef = useRef(null);
  const sectionRefs = useRef({});
  const [selected, setSelected] = useState(null);

  const [yes, setYes] = useState(false);
  const [noPos, setNoPos] = useState({ x: 0, y: 0 });
  const [noMoves, setNoMoves] = useState(0);

  const [vw, setVw] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024,
  );
  const isMobile = vw < 768;

  const dias = daysBetween(FECHA_INICIO);

  // ===== INTRO POPUP (video vertical) =====
  const [showIntro, setShowIntro] = useState(true);
  const introVideoRef = useRef(null);
  const [needsTap, setNeedsTap] = useState(false);

  const closeIntro = () => {
    setShowIntro(false);
    setNeedsTap(false);
  };

  const openIntro = () => {
    setNeedsTap(false);
    setShowIntro(true);
  };

  // Bloquea scroll mientras el popup está activo
  useEffect(() => {
    if (!showIntro) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showIntro]);

  // Autoplay: intentamos reproducir al abrir; si lo bloquea, mostramos botón “Toca”
  useEffect(() => {
    if (!showIntro) return;

    const t = setTimeout(async () => {
      const v = introVideoRef.current;
      if (!v) return;

      try {
        v.currentTime = 0;
        v.muted = true;
        const p = v.play();
        if (p?.catch) await p;
      } catch (e) {
        setNeedsTap(true);
      }
    }, 120);

    // Fallback (por si no dispara ended)
    const fallback = setTimeout(() => closeIntro(), 6500);

    return () => {
      clearTimeout(t);
      clearTimeout(fallback);
    };
  }, [showIntro]);

  // ===== Hearts =====
  const hearts = useMemo(() => {
    const count = isMobile ? 10 : 18;
    return Array.from({ length: count }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      size: (isMobile ? 10 : 12) + Math.random() * (isMobile ? 14 : 18),
      delay: Math.random() * 2.5,
      duration: (isMobile ? 7 : 6) + Math.random() * 6,
      opacity: 0.1 + Math.random() * 0.18,
      rotate: Math.random() * 40 - 20,
    }));
  }, [isMobile]);

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // IntersectionObserver para nav activo
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(Number(entry.target.dataset.key));
        }),
      { rootMargin: "-55% 0px -40% 0px", threshold: 0 },
    );

    sections.forEach((s) => {
      const el = sectionRefs.current[s.key];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollTo = (key) => {
    const el = sectionRefs.current[key];
    const navH = navRef.current?.offsetHeight || 0;
    if (!el) return;
    window.scrollTo({
      top: el.offsetTop - navH - 10,
      behavior: "smooth",
    });
  };

  const moveNoButton = () => {
    const maxX = Math.min(
      120,
      Math.floor(
        (typeof window !== "undefined" ? window.innerWidth : 360) * 0.2,
      ),
    );
    const maxY = isMobile ? 35 : 70;
    const x = Math.floor((Math.random() * 2 - 1) * maxX);
    const y = Math.floor((Math.random() * 2 - 1) * maxY);
    setNoPos({ x, y });
    setNoMoves((m) => m + 1);
  };

  return (
    <div
      className="min-h-screen w-full bg-cover bg-center bg-scroll md:bg-fixed"
      style={{
        backgroundImage: `url(${mardeolivosfondo})`,
        backgroundColor: "#fdfaf4",
      }}
    >
      {/* Corazones flotando */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        {hearts.map((h) => (
          <motion.span
            key={h.id}
            className="absolute select-none"
            style={{
              left: `${h.left}%`,
              bottom: `-40px`,
              fontSize: `${h.size}px`,
              opacity: h.opacity,
              transform: `rotate(${h.rotate}deg)`,
            }}
            initial={{ y: 0, opacity: 0 }}
            animate={{ y: -900, opacity: [0, h.opacity, 0] }}
            transition={{
              delay: h.delay,
              duration: h.duration,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            💛
          </motion.span>
        ))}
      </div>

      {/* INTRO POPUP: video vertical autoplay, desaparece al acabar */}
      <AnimatePresence>
        {showIntro && (
          <motion.div
            className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
          >
            <motion.div
              className="relative w-[min(88vw,420px)] aspect-[9/16] rounded-3xl overflow-hidden border-2 border-[#C19B50] bg-black shadow-2xl"
              initial={{ opacity: 0, scale: 0.92, y: 18 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 18 }}
              transition={{ duration: 0.55, ease: "easeInOut" }}
            >
              <button
                onClick={closeIntro}
                className="absolute top-3 right-3 z-10 px-3 py-1 rounded-full bg-white/85 border border-[#C19B50] text-[#503a2c] text-xs font-extrabold shadow"
              >
                Saltar ✕
              </button>

              <video
                ref={introVideoRef}
                src={videoSrc}
                className="w-full h-full object-cover"
                playsInline
                muted
                autoPlay
                preload="auto"
                onEnded={closeIntro}
              />

              {needsTap && (
                <button
                  onClick={async () => {
                    try {
                      const v = introVideoRef.current;
                      if (!v) return;
                      v.currentTime = 0;
                      v.muted = true;
                      await v.play();
                      setNeedsTap(false);
                    } catch (e) {
                      setNeedsTap(true);
                    }
                  }}
                  className="absolute inset-0 z-10 flex items-center justify-center"
                >
                  <span className="px-4 py-2 rounded-full bg-white/85 border border-[#C19B50] text-[#503a2c] font-extrabold shadow">
                    Toca para ver 💛
                  </span>
                </button>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top nav */}
      <nav
        ref={navRef}
        className="fixed top-0 left-0 right-0 z-50 bg-white/75 shadow-md backdrop-blur-sm"
      >
        <div className="h-1 bg-[#ba913d] w-full" />
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-2 gap-4">
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-bold text-[#503a2c]">💌</span>
            <span className="text-lg font-bold text-[#503a2c]">
              {PAGE_NAME}
            </span>
            <span className="text-sm text-[#ba913d] hidden sm:inline">
              Para {NOMBRE} (edición San Valentín)
            </span>
          </div>

          <div className="flex items-center gap-5 overflow-x-auto whitespace-nowrap">
            {sections.map((s) => (
              <button
                key={s.key}
                onClick={() => scrollTo(s.key)}
                className={`text-base font-semibold pb-1 transition ${
                  active === s.key
                    ? "text-[#ba913d] border-b-2 border-[#ba913d]"
                    : "text-[#503a2c]"
                }`}
              >
                {s.nombre}
              </button>
            ))}
            <Link
              to="/"
              className="text-sm font-semibold text-[#503a2c] opacity-80 hover:opacity-100"
              title="Volver"
            >
              Volver ↩
            </Link>
          </div>
        </div>
      </nav>

      <div className="h-20" />

      <div className="max-w-6xl mx-auto px-4 pb-16">
        {/* HERO: sin vídeo (portada + botón para reabrir intro) */}
        <section
          id="peli"
          data-key={1}
          ref={(el) => (sectionRefs.current[1] = el)}
          className="pt-4 scroll-mt-24"
        >
          <div className="w-full bg-white/70 rounded-3xl shadow-lg backdrop-blur-sm overflow-hidden">
            <div className="px-6 py-3 bg-white/75 border-b-2 border-b-[#C19B50]">
              <h1 className="text-3xl md:text-4xl font-extrabold text-[#503a2c]">
                Feliz San Valentín, {NOMBRE} 💛
              </h1>
              <p className="text-[#503a2c] opacity-90 mt-1">
                Llevamos{" "}
                <span className="font-bold text-[#ba913d]">{dias}</span> días
                siendo equipo. Y me sigues gustando como el primer día (o más).
              </p>
            </div>

            <div className="p-4 md:p-6">
              <div className="min-h-[calc(100vh-140px)] flex flex-col justify-center">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 items-center">
                  {/* Izquierda */}
                  <div className="order-2 lg:order-1">
                    <div className="bg-white/75 rounded-3xl border border-[#C19B50] p-5">
                      <p className="text-[#503a2c] text-lg font-semibold">
                        Tres cosas que quiero hoy:
                      </p>

                      <ul className="mt-3 space-y-2 text-[#503a2c]">
                        <li className="flex gap-2">
                          <span>✨</span>
                          <span>
                            Que te rías aunque sea un poquito (vale, mucho).
                          </span>
                        </li>
                        <li className="flex gap-2">
                          <span>🍽️</span>
                          <span>
                            Que el “menú del amor” te haga decir: “este niño…”
                          </span>
                        </li>
                        <li className="flex gap-2">
                          <span>🫶</span>
                          <span>
                            Que recuerdes: contigo, todo me sale mejor.
                          </span>
                        </li>
                      </ul>

                      <div className="mt-4 flex flex-wrap items-center gap-3">
                        <button
                          onClick={() => scrollTo(2)}
                          className="px-4 py-2 rounded-full bg-[#ba913d] text-white font-bold shadow hover:opacity-95"
                        >
                          Ir al menú 💛
                        </button>
                        <button
                          onClick={() => scrollTo(4)}
                          className="px-4 py-2 rounded-full border border-[#503a2c] text-[#503a2c] font-bold bg-white/70 hover:bg-white"
                        >
                          Ver el plan 😏
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Centro: Portada Laurita */}
                  <motion.div
                    className="order-1 lg:order-2 relative mx-auto w-full max-w-[560px] rounded-3xl overflow-hidden border-2 border-[#C19B50] bg-white/60 shadow-lg"
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, ease: "easeInOut" }}
                  >
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute -top-10 -left-10 w-44 h-44 rounded-full bg-[#ba913d]/10 blur-2xl" />
                      <div className="absolute -bottom-10 -right-10 w-44 h-44 rounded-full bg-[#503a2c]/10 blur-2xl" />
                    </div>

                    <div className="p-6 md:p-8 text-center">
                      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 border border-[#C19B50] text-[#503a2c] text-xs font-extrabold shadow">
                        💌 Laurita · edición San Valentín
                      </div>

                      <motion.div
                        className="mt-5 text-6xl"
                        animate={{ scale: [1, 1.06, 1] }}
                        transition={{
                          duration: 1.8,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                      >
                        💛
                      </motion.div>

                      <h2 className="mt-4 text-2xl md:text-3xl font-extrabold text-[#503a2c]">
                        Feliz San Valentin mi amor 💛
                      </h2>

                      <p className="mt-2 text-[#503a2c] opacity-90">
                        Dale y te sale el vídeo como “sorpresa” otra vez 🙂
                      </p>

                      <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
                        <button
                          onClick={openIntro}
                          className="w-full sm:w-auto px-5 py-3 rounded-full bg-[#ba913d] text-white font-extrabold shadow hover:opacity-95"
                        >
                          Ver el vídeo otra vez 🎬
                        </button>

                        <button
                          onClick={() => scrollTo(2)}
                          className="w-full sm:w-auto px-5 py-3 rounded-full border border-[#503a2c] text-[#503a2c] font-extrabold bg-white/80 hover:bg-white"
                        >
                          Ir al menú 💛
                        </button>
                      </div>

                      <div className="mt-5 text-xs text-[#503a2c] opacity-75">
                        💛💛💛💛💛
                      </div>
                    </div>
                  </motion.div>

                  {/* Derecha */}
                  <div className="order-3">
                    <div className="bg-white/60 rounded-3xl border border-[#C19B50]/70 p-4">
                      <p className="text-sm text-[#503a2c] opacity-90">
                        Pista: si te preguntas “¿quién ha hecho esta web?”… sí,
                        el pesado de tu novio. Y a mucha honra.
                      </p>
                    </div>

                    <div className="mt-3 bg-white/75 rounded-3xl border border-[#C19B50] p-4">
                      <p className="text-[#503a2c] font-semibold">
                        Mini-reto:
                        <span className="text-[#ba913d] font-extrabold">
                          {" "}
                          sonríe{" "}
                        </span>
                        ahora 🙂
                      </p>
                      <p className="text-sm text-[#503a2c] opacity-85 mt-1">
                        (Si lo consigues, desbloqueas premio en “El plan”).
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-center">
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-xs text-[#503a2c] opacity-70 bg-white/60 border border-[#C19B50]/60 px-4 py-2 rounded-full"
                  >
                    Desliza abajo para el menú 💛
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* MENÚ */}
        <section
          id="menu"
          data-key={2}
          ref={(el) => (sectionRefs.current[2] = el)}
          className="pt-6 scroll-mt-24"
        >
          <div className="w-full bg-white/70 shadow-lg backdrop-blur-sm overflow-hidden rounded-3xl">
            <div className="px-6 py-3 bg-white/75 border-t-2 border-b-2 border-t-[#C19B50] border-b-[#C19B50]">
              <h2 className="text-3xl font-bold text-[#503a2c]">
                Menú del Amor
              </h2>
              <p className="text-[#503a2c] opacity-90">
                (Toca cualquier “plato” para ver el detalle)
              </p>
            </div>

            <div className="p-4 md:p-6 space-y-5">
              {menuDelAmor.map((cat, idx) => (
                <motion.div
                  key={cat.title}
                  className="bg-white/65 rounded-3xl border border-[#C19B50] overflow-hidden"
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ delay: idx * 0.08, duration: 0.45 }}
                >
                  <div className="px-5 py-3 bg-white/70 border-b border-[#C19B50]/70">
                    <h3 className="text-xl font-extrabold text-[#503a2c]">
                      {cat.title}
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3">
                    {cat.items.map((it) => (
                      <button
                        key={it.name}
                        onClick={() => setSelected(it)}
                        className="text-left p-4 rounded-2xl bg-white/75 border border-[#C19B50]/70 hover:bg-white transition shadow-sm"
                      >
                        <div className="flex items-start gap-3">
                          <div className="text-2xl">{it.emoji}</div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-lg font-bold text-[#503a2c]">
                                {it.name}
                              </p>
                              <span className="text-sm font-bold text-[#ba913d]">
                                {it.price}
                              </span>
                            </div>
                            <p className="text-sm text-black/80 mt-1">
                              {it.desc}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* RAZONES */}
        <section
          id="razones"
          data-key={3}
          ref={(el) => (sectionRefs.current[3] = el)}
          className="pt-6 scroll-mt-24"
        >
          <div className="w-full bg-white/70 rounded-3xl shadow-lg backdrop-blur-sm overflow-hidden">
            <div className="px-6 py-3 bg-white/75 border-t-2 border-b-2 border-t-[#C19B50] border-b-[#C19B50]">
              <h2 className="text-3xl font-bold text-[#503a2c]">
                Razones por las que te elijo
              </h2>
            </div>

            <div className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  t: "Tu manera de estar",
                  d: "Haces hogar en cualquier sitio. Y yo quiero vivir ahí.",
                  e: "🏡",
                },
                {
                  t: "Tu risa",
                  d: "Es mi notificación favorita. Y no la silencio nunca.",
                  e: "😂",
                },
                {
                  t: "Tu corazón",
                  d: "Te preocupa todo el mundo… y yo tengo suerte de estar dentro.",
                  e: "💛",
                },
              ].map((c, i) => (
                <motion.div
                  key={c.t}
                  className="bg-white/75 rounded-3xl border border-[#C19B50] p-5 shadow-sm"
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ delay: i * 0.08, duration: 0.45 }}
                >
                  <div className="text-3xl">{c.e}</div>
                  <h3 className="text-xl font-extrabold text-[#503a2c] mt-2">
                    {c.t}
                  </h3>
                  <p className="text-[#503a2c] opacity-90 mt-2">{c.d}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* EL PLAN + MINIJUEGO */}
        <section
          id="plan"
          data-key={4}
          ref={(el) => (sectionRefs.current[4] = el)}
          className="pt-6 scroll-mt-24"
        >
          <div className="w-full bg-white/70 rounded-3xl shadow-lg backdrop-blur-sm overflow-hidden">
            <div className="px-6 py-3 bg-white/75 border-t-2 border-b-2 border-t-[#C19B50] border-b-[#C19B50]">
              <h2 className="text-3xl font-bold text-[#503a2c]">El plan 😏</h2>
              <p className="text-[#503a2c] opacity-90">
                Última pregunta (de verdad, la última… creo)
              </p>
            </div>

            <div className="p-4 md:p-6">
              <div className="bg-white/75 rounded-3xl border border-[#C19B50] p-5">
                <p className="text-[#503a2c] text-lg font-semibold">
                  ¿Quieres ser mi San Valentín hoy y mañana y pasado y…?
                </p>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => setYes(true)}
                    className="px-5 py-2 rounded-full bg-[#ba913d] text-white font-extrabold shadow hover:opacity-95"
                  >
                    Sí 💛
                  </button>

                  <motion.button
                    onMouseEnter={moveNoButton}
                    onClick={moveNoButton}
                    className="px-5 py-2 rounded-full border border-[#503a2c] text-[#503a2c] font-extrabold bg-white/80"
                    animate={{ x: noPos.x, y: noPos.y }}
                    transition={{ type: "spring", stiffness: 220, damping: 16 }}
                  >
                    No 🙃
                  </motion.button>

                  <span className="text-sm text-[#503a2c] opacity-80">
                    {noMoves > 0 ? `Intentos de “No”: ${noMoves} 😄` : ""}
                  </span>
                </div>

                <AnimatePresence>
                  {yes && (
                    <motion.div
                      className="mt-5 p-4 rounded-2xl bg-white/80 border border-[#C19B50]"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                    >
                      <p className="text-[#503a2c] font-bold text-lg">
                        Sabía que sí 😌
                      </p>
                      <p className="text-[#503a2c] opacity-90 mt-1">
                        Premio: esta noche elijo yo… pero tú mandas igual, como
                        siempre ;).
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="mt-4 text-center text-sm text-[#503a2c] opacity-80">
                Hecho con amor, café, y miedo a que me digas “qué wenwensita”
                ☕🎨
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Modal del “plato” */}
      <AnimatePresence>
        {selected && (
          <motion.div
            className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
          >
            <motion.div
              className="relative bg-white rounded-3xl overflow-hidden w-full max-w-lg border-2 border-[#C19B50]"
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelected(null)}
                className="absolute top-3 right-3 bg-[#503a2c] text-white rounded-full w-9 h-9 flex items-center justify-center shadow"
                title="Cerrar"
              >
                ✕
              </button>

              <div className="px-6 py-4 bg-white/90 border-b border-[#C19B50]/70">
                <div className="flex items-center justify-between gap-4">
                  <h3 className="text-2xl font-extrabold text-[#503a2c]">
                    {selected.emoji} {selected.name}
                  </h3>
                  <span className="text-sm font-extrabold text-[#ba913d]">
                    {selected.price}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <p className="text-[#503a2c] opacity-95 text-lg">
                  {selected.desc}
                </p>
                <p className="mt-4 text-sm text-[#503a2c] opacity-80">
                  Nota del chef: si sonríes ahora, la propina es obligatoria (un
                  beso).
                </p>

                <div className="mt-5 flex justify-end">
                  <button
                    onClick={() => setSelected(null)}
                    className="px-4 py-2 rounded-full bg-[#ba913d] text-white font-bold shadow"
                  >
                    Vale, me lo pido 💛
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
